import { ACL, ROLES } from "./Acl";
import { AppointmentID } from "./Appointment";
import { STATUS_IDENTIFIERS } from "./AppointmentStatus";
import { Brand, BrandGroup, BrandGroupID } from "./Brand";
import { CAR_TYPE } from "./Car";
import { Dealer, DealerID } from "./Dealer";
import { DealerLocation, DealerLocationID } from "./DealerLocation";
import { DeviceID } from "./Device";
import { ExportEvent } from "./ExportEvent";
import { LANGUAGE_CODES } from "./Language";
import { NotificationElementID } from "./Notifications";
import { OneiPlanningMonteurID } from "./Oneiplanning";
import { PlanItPlanningMechanicID } from "./Planitplanning";
import { DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS } from "./UserFilter";

export type UserID = number;

export type NetworkMetricsID = number;

export enum USER_STATUSES {
  UserActive = 1,
  UserInactive = 2,
  UserDeleted = 3
}

export enum LANDING_PAGES {
  Main = 1,
  LeadDashboard = 2,
  WarrantyDashboard = 3,
  ManufacturerDashboard = 4,
  DayplannerDashboard = 5,
  ExactMonitoringDashboard = 6
}

export interface LandingPage {
  id: LANDING_PAGES;
  name: string;
}

export interface User {
  created_on: string;
  updated_on: string;
  id: UserID; // Claire User ID
  dms_nr: string | null; // DMS Number of the Claire User
  dms_location_source: string; // DMS Location Source
  external_user_id: string; // External User ID
  first_name: string; // First name
  last_name: string; // Last name
  email: string; // Email in Claire
  username: string; // Username in Claire
  mobile: string; // Mobile phone numer
  phone: string; // Phone numer
  birthday?: string; // Birthdate
  last_active?: string; // Last activity date in Claire
  address1: string; // Address line 1
  address2: string; // Address line 2
  last_password_change?: string; // Last password change date in Claire
  status: USER_STATUSES; // User status
  web_version: string; // Web version the user is using
  app_version: string; // App version the user is using
  camera_version: string; // Camera app version the user is using
  location_column_visible: boolean; // Is Location Visible
  multi_device_login_enabled: boolean; // Is user allowed to login from multiple devices simultaneously
  is_combi_user: boolean; // Indicates if a user can use both web and tablet
  has_leased_tablet: boolean; // Indicates if the user has a tablet that is used
  list_appointments_by_scheduled_range: boolean; // Indicates if a user can list appointments by scheduled range
  csv_download: boolean; // Indicates if a user can download CSV files
  profile_picture: string; // Profile picture URL
  language_code: LANGUAGE_CODES; // Default language for the user
  dealer_id: DealerID; // Default Dealer ID when loggin in
  dealer_location_id: DealerLocationID; // Default Location ID when loggin in
  role_id: ROLES; // Default Role ID when loggin in
  landing_page_id: LANDING_PAGES; // Default Landing Page ID when loggin in
  is_counter_tablet_user: boolean; // Indicates if a user is a counter tablet user
  is_keylocker_allowed: boolean; // Indicates if a user is allowed to use key locker
  is_tablet_translation_ai_enabled: boolean; // Indicates if a user can use AI translations on the tablet
  is_available: boolean;
  pin_status_notification_enabled: boolean; // Indicates if a user is allowed to receive pin status notifications
  customer_waiting_car_in_shop_notification_enabled: boolean; // If true, user will get notified by claire notification for workorder with car in shop and customer waiting status.
  tyre_ordering_enabled: boolean; // Indicates if a user is allowed to order tyres
  shop_status_same_day_filter_enabled: boolean; // Indicates if same day shop status filter is enabled
  shop_status_next_day_filter_enabled: boolean; // Indicates if next day shop status filter is enabled
  shop_status_to_order_filter_enabled: boolean; // Indicates if to order shop status filter is enabled
  shop_status_backorder_filter_enabled: boolean; // Indicates if backorder shop status filter is enabled
  shop_status_arrived_parts_filter_enabled: boolean; // Indicates if arrived parts shop status filter is enabled
  star_color_green_enabled: boolean; // Indicates if filtering on green star color is enabled
  star_color_red_enabled: boolean; // Indicates if filtering on red star color is enabled
  star_color_blue_enabled: boolean; // Indicates if filtering on blue star color is enabled
  star_color_yellow_enabled: boolean; // Indicates if filtering on yellow star color is enabled
  working_on: AppointmentID | null; // ID of the appointment the user is currently working on
  day_planner_appointments_collapsed_by_default: boolean; // Indicates if appointments are collapsed by default on the dayplanner.
  day_planner_cars_ready_collapsed_by_default: boolean; // Indicates if cars ready are collapsed by default on the dayplanner.
  day_planner_quality_control_collapsed_by_default: boolean; // Indicates if quality control is collapsed by default on the dayplanner.
  car_type_filter: CAR_TYPE | null; // Filter for what type of vehicle is visible in main/dayoverview page
  is_vat_enabled_on_wo: boolean; // if vat is enabled at location level then this flag is the default value for the vat toggle on appointment detail pages.
  is_desktop_notifications_enabled: boolean; // Indicates if desktop notifications are enabled. Note: This field is not used by V7.
  my_locations?: DealerLocationID[]; // Locations the user has access to
  dealers?: Dealer[]; // Dealers the user has access to
  dealers_ids?: DealerID[]; // Dealer IDs the user has access to
  location_ids?: DealerLocationID[]; // Location IDs the user has access to
  dealer_location?: DealerLocation; // Dealer location the user has access to
  device_id?: DeviceID; // Device ID of the user
  counter_tablet_key?: string; // come from the auth user, not saved in the DB to last only as long as the session
  role_name?: string; // Role name of the user
  dpo?: boolean; // Indicates if the user is a DPO user
  acl?: ACL[]; // ACLs of the user
  brand_group_ids?: BrandGroupID[]; // List of brand group identifiers
  brand_groups?: BrandGroup[]; // List of brand groups
  onei_planning_monteur_id?: OneiPlanningMonteurID;
  planit_planning_mechanic_id?: PlanItPlanningMechanicID; // PlanIt Planning Mechanic ID of the user
  notification_element_ids?: NotificationElementID[]; // Notification element IDs of the user
  dealer_name?: string; // Name of the dealer
  location_name?: string; // Name of the location
  appointment_id?: AppointmentID; // AppointmentID, used internally
  appointment_status_identifiers?: STATUS_IDENTIFIERS[]; // StatusIdentifiers filters of the user
  dayplanner_appointment_status_identifiers?: DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS[]; // StatusIdentifiers filters of the user on the Dayplanner
  dayplanner_brand_group_ids?: BrandGroupID[]; // List of brandGroupIDs of the user on the Dayplanner
  has_leased_device?: boolean; // Indicates if the user has a leased device
  export_events?: ExportEvent[]; // List of exportEvents for the user that have not been downloaded yet
}

export interface NetworkMetrics {
  created_on: string;
  updated_on: string;
  id: NetworkMetricsID;
  ping_claire: number;
  ping_outside: number;
  delay_claire: number;
  delay_outside: number;
  wifi_strength: number;
  wifi_name: string;
  user_id: UserID;
  dealer_location_id: DealerLocationID;
}

export interface AuthUser {
  id: UserID;
  fullname: string;
  brands?: Brand[];
}

export interface DMSUser {
  created_on: string;
  updated_on: string;
  dealer_location_id: DealerLocationID; // dealer location id of the dms user
  dms_nr: string; // dms nr of the user
  name: string; // name of the user
  user_id: UserID | null; // Claire User ID of the DMS User
}
